/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Layout } from './components/Layout';
import { SmartImage } from './components/SmartImage';
import { SEO, breadcrumbJsonLd, orgJsonLd, siteJsonLd } from './components/SEO';
import { ArrowRight, BookOpen, Calculator, Clock, FileCheck2, Loader2, Search, Globe, ShieldCheck } from 'lucide-react';
import { CORNERSTONE_ARTICLE_SUMMARIES } from './data/cornerstoneArticleSummaries';
import { MARKET_PROFILES, PRIMARY_MARKET_CODES, getMarketByRouteKey } from './data/localization';
import { SEO_TOPIC_CLUSTERS, getTopicClusterPath } from './data/seoTopicClusters';

const ArticleList = lazy(() => import('./components/ArticleList').then((module) => ({ default: module.ArticleList })));
const ArticleDetail = lazy(() => import('./components/ArticleDetail').then((module) => ({ default: module.ArticleDetail })));
const AdminLogin = lazy(() => import('./components/AdminLogin').then((module) => ({ default: module.AdminLogin })));
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then((module) => ({ default: module.AdminDashboard })));
const ArticleEditor = lazy(() => import('./components/ArticleEditor').then((module) => ({ default: module.ArticleEditor })));
const CategoryManager = lazy(() => import('./components/CategoryManager').then((module) => ({ default: module.CategoryManager })));
const CalculatorManager = lazy(() => import('./components/CalculatorManager').then((module) => ({ default: module.CalculatorManager })));
const JurisdictionManager = lazy(() => import('./components/JurisdictionManager').then((module) => ({ default: module.JurisdictionManager })));
const SeoIntelligence = lazy(() => import('./components/SeoIntelligence').then((module) => ({ default: module.SeoIntelligence })));
const ToolsDirectory = lazy(() => import('./components/ToolsDirectory').then((module) => ({ default: module.ToolsDirectory })));
const ToolPage = lazy(() => import('./components/ToolPage').then((module) => ({ default: module.ToolPage })));
const ReportPage = lazy(() => import('./components/ReportPage').then((module) => ({ default: module.ReportPage })));
const TopicClusterList = lazy(() => import('./components/TopicClusterPage').then((module) => ({ default: module.TopicClusterList })));
const TopicClusterPage = lazy(() => import('./components/TopicClusterPage').then((module) => ({ default: module.TopicClusterPage })));
const AboutUs = lazy(() => import('./components/AboutUs').then((module) => ({ default: module.AboutUs })));
const AboutData = lazy(() => import('./components/AboutData').then((module) => ({ default: module.AboutData })));
const EditorialPolicy = lazy(() => import('./components/EditorialPolicy').then((module) => ({ default: module.EditorialPolicy })));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy').then((module) => ({ default: module.PrivacyPolicy })));
const CookiePolicy = lazy(() => import('./components/CookiePolicy').then((module) => ({ default: module.CookiePolicy })));
const TermsOfService = lazy(() => import('./components/TermsOfService').then((module) => ({ default: module.TermsOfService })));
const Contact = lazy(() => import('./components/Contact').then((module) => ({ default: module.Contact })));

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const LegacyKnowledgeRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/guides/${slug || ''}`} replace />;
};

const EuropeGuidesPageRedirect = () => {
  const { pageNumber } = useParams<{ pageNumber: string }>();
  return <Navigate to={`/europe/guides/page/${pageNumber || '1'}`} replace />;
};

const AnalyticsPageView = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined' || !window.gtag) return;

    window.gtag('event', 'page_view', {
      page_path: `${pathname}${search}`,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);

  return null;
};

const getPageName = (pathname: string) => {
  if (pathname === '/') return 'Home';
  if (pathname === '/knowledge') return 'Guides';
  if (pathname.startsWith('/knowledge/')) return 'Guide';
  if (pathname === '/tools') return 'Tools';
  if (pathname.startsWith('/tools/')) return 'Tool';
  if (pathname === '/topics') return 'Topic Hubs';
  if (pathname.startsWith('/topics/')) return 'Topic Hub';
  if (pathname === '/guides') return 'Guides';
  if (/^\/guides\/page\/\d+/.test(pathname)) return 'Guides';
  if (pathname.startsWith('/guides/')) return 'Guide';
  if (pathname === '/africa/guides') return 'Archived Regional Guides';
  if (/^\/africa\/guides\/page\/\d+/.test(pathname)) return 'Archived Regional Guides';
  if (pathname === '/us') return 'United States';
  if (pathname === '/uk') return 'United Kingdom';
  if (pathname === '/canada') return 'Canada';
  if (pathname === '/europe' || pathname === '/eu') return 'Europe';
  if (pathname === '/de') return 'Germany';
  if (pathname === '/fr') return 'France';
  if (pathname === '/es') return 'Spain';
  if (pathname === '/zm') return 'Archived Market';
  const marketMatch = pathname.match(/^\/(us|uk|canada|europe|eu|de|fr|es|zm)(?:\/(tools|guides))?/);
  if (marketMatch) {
    const market = getMarketByRouteKey(marketMatch[1]);
    if (marketMatch[2] === 'tools') return `${market.countryName} Calculators`;
    if (marketMatch[2] === 'guides') return `${market.countryName} Guides`;
    return market.countryName;
  }
  if (pathname.endsWith('/tools')) return 'Tools';
  if (pathname.includes('/tools/')) return 'Tool';
  if (pathname.endsWith('/guides')) return 'Guides';
  if (pathname.includes('/guides/')) return 'Guide';
  if (pathname.includes('/reports/')) return 'Report';
  if (pathname === '/about') return 'About';
  if (pathname.startsWith('/africa/guides/')) return 'Guide';
  if (pathname === '/about-the-data') return 'Methodology';
  if (pathname === '/methodology') return 'Methodology';
  if (pathname === '/editorial-policy') return 'Editorial Policy';
  if (pathname === '/privacy-policy') return 'Privacy Policy';
  if (pathname === '/cookie-policy') return 'Cookie Policy';
  if (pathname === '/terms-of-service') return 'Terms Of Service';
  if (pathname === '/contact') return 'Contact';
  if (pathname === '/admin/login') return 'Admin Login';
  if (pathname === '/admin/dashboard') return 'Admin Dashboard';
  if (pathname === '/admin/new') return 'New Article';
  if (pathname.startsWith('/admin/edit/')) return 'Edit Article';
  if (pathname === '/admin/categories') return 'Category Manager';
  if (pathname === '/admin/calculators') return 'Calculator Manager';
  if (pathname === '/admin/jurisdictions') return 'Jurisdiction Manager';
  return 'Page';
};

const RouteMeta = () => {
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith('/admin');
  const isReportRoute = pathname.includes('/reports/');
  const isDeemphasizedRoute = pathname === '/zm' || pathname.startsWith('/zm/') || pathname === '/africa/guides' || pathname.startsWith('/africa/guides/');
  const pageName = getPageName(pathname);
  const title = pageName === 'Home' ? 'Financial Calculators for North America & Europe' : pageName;
  const description = isReportRoute
    ? 'Private shareable FinancialMetrics.report calculation report.'
    : 'FinancialMetrics.report provides localized financial calculators and guides for US, Canada, UK, and European tax, salary, VAT, mortgage, debt, savings, investing, and business scenarios.';

  return (
    <SEO
      title={title}
      description={description}
      path={pathname}
      robots={isAdminRoute ? 'noindex, nofollow' : isReportRoute || isDeemphasizedRoute ? 'noindex, follow' : 'index, follow, max-image-preview:large'}
      jsonLd={pathname === '/' ? [orgJsonLd, siteJsonLd, breadcrumbJsonLd([{ name: 'Home', url: '/' }])] : []}
    />
  );
};

const RouteFallback = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-slate-300" aria-label="Loading page" />
  </div>
);

const LATEST_GUIDES_COUNT = 18;
const LATEST_GUIDES_FETCH_COUNT = 500;

const fallbackLatestGuides = CORNERSTONE_ARTICLE_SUMMARIES.map((article, index) => ({
  ...article,
  id: `cornerstone-${article.slug}`,
  route: article.route || `/guides/${article.slug}`,
  status: 'Published',
  scopeType: article.scopeType || 'Global',
  countryId: article.countryId,
  categoryName: article.category,
  publishedAt: { seconds: Math.floor(new Date('2026-09-02').getTime() / 1000) - index },
})).filter((article) => article.countryId !== 'zm' && !article.route?.startsWith('/zm/') && !article.route?.startsWith('/africa/guides'));

const Home = () => {
  const [latestArticles, setLatestArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [shouldLoadLatest, setShouldLoadLatest] = useState(false);
  const latestSectionRef = useRef<HTMLElement | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/guides?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  useEffect(() => {
    if (shouldLoadLatest) return;

    let frame = 0;

    const loadWhenNearViewport = () => {
      if (frame) return;

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const section = latestSectionRef.current;
        if (!section) return;

        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight + 200 && rect.bottom > -200) {
          setShouldLoadLatest(true);
        }
      });
    };

    window.addEventListener('scroll', loadWhenNearViewport, { passive: true });
    window.addEventListener('resize', loadWhenNearViewport);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', loadWhenNearViewport);
      window.removeEventListener('resize', loadWhenNearViewport);
    };
  }, [shouldLoadLatest]);

  useEffect(() => {
    if (!shouldLoadLatest) return;

    let isMounted = true;

    const fetchLatest = async () => {
      setLoading(true);
      try {
        const [{ collection, query, limit, getDocs }, { db }] = await Promise.all([
          import('firebase/firestore'),
          import('./firebase'),
        ]);
        const q = query(
          collection(db, 'articles'),
          limit(LATEST_GUIDES_FETCH_COUNT)
        );
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
        const publishedData = data
          .filter((a: any) => a.status === 'Published' || a.published === true)
          .sort((a: any, b: any) => {
            const dateA = a.publishedAt?.seconds || 0;
            const dateB = b.publishedAt?.seconds || 0;
            return dateB - dateA;
          });
        const fallbackBySlug = new Map(fallbackLatestGuides.map((article) => [article.slug, article]));
        const enrichedPublishedData = publishedData.map((article: any) => {
          const curatedArticle = article.slug ? fallbackBySlug.get(article.slug) : undefined;
          if (!curatedArticle) return article;

          return {
            ...article,
            ...curatedArticle,
            id: article.id || curatedArticle.id,
            status: 'Published',
            published: true,
          };
        });
        const existingSlugs = new Set(enrichedPublishedData.map((article: any) => article.slug));
        const filledArticles = [
          ...enrichedPublishedData,
          ...fallbackLatestGuides.filter((article) => !existingSlugs.has(article.slug)),
        ].slice(0, LATEST_GUIDES_COUNT);
        if (isMounted) setLatestArticles(filledArticles);
      } catch (error) {
        console.error("Error fetching latest articles:", error);
        if (isMounted) {
          setLatestArticles(fallbackLatestGuides.slice(0, LATEST_GUIDES_COUNT));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchLatest();

    return () => {
      isMounted = false;
    };
  }, [shouldLoadLatest]);

  const getArticleImage = (article: any) => {
    if (article.coverImage || article.featuredImageUrl) {
      return article.coverImage || article.featuredImageUrl;
    }

    const markdownImage = article.content?.match(/!\[.*?\]\((.*?)\)/);
    return markdownImage?.[1];
  };

  const platformPillars = [
    {
      title: 'Calculate the number',
      body: 'Use calculators for VAT, sales tax, salary, debt, loans, savings, investments, retirement, and business metrics across North America and Europe.',
      icon: Calculator,
    },
    {
      title: 'Understand the formula',
      body: 'Read the supporting guide to see formulas, worked examples, assumptions, limitations, and common mistakes.',
      icon: BookOpen,
    },
    {
      title: 'Check the source',
      body: 'Market pages point back to official or reputable sources when tax, payroll, consumer finance, or statutory rules matter.',
      icon: ShieldCheck,
    },
  ];

  const topicClusters = [
    {
      title: 'Tax and payroll',
      links: [
        { label: 'Europe VAT Calculator', to: '/tools/vat-calculator' },
        { label: 'US Income Tax Calculator', to: '/us/tools/income-tax-calculator' },
        { label: 'UK Salary Calculator', to: '/uk/tools/salary-calculator' },
      ],
    },
    {
      title: 'Cash resilience',
      links: [
        { label: 'Emergency Fund Calculator', to: '/tools/emergency-fund-calculator' },
        { label: 'Net Worth Calculator', to: '/tools/net-worth-calculator' },
        { label: 'Budget Calculator', to: '/tools/budget-calculator' },
      ],
    },
    {
      title: 'Debt readiness',
      links: [
        { label: 'UK Credit Card Minimum Payment', to: '/uk/tools/credit-card-minimum-payment-calculator' },
        { label: 'Debt-to-Income Calculator', to: '/tools/debt-to-income' },
        { label: 'Debt Payoff Calculator', to: '/tools/debt-payoff-calculator' },
      ],
    },
    {
      title: 'Business planning',
      links: [
        { label: 'Break-Even Calculator', to: '/tools/break-even-calculator' },
        { label: 'Margin Calculator', to: '/tools/margin-calculator' },
        { label: 'Markup Calculator', to: '/tools/markup-calculator' },
      ],
    },
    {
      title: 'Investing and inflation',
      links: [
        { label: 'Inflation Impact Calculator', to: '/tools/inflation-impact' },
        { label: 'APR to APY Calculator', to: '/tools/apr-to-apy-calculator' },
        { label: 'Compound Interest Calculator', to: '/tools/compound-interest' },
      ],
    },
  ];
  const priorityFeaturedClusterSlugs = new Set([
    'uk-credit-card-debt-minimum-payment-persistent-debt',
  ]);
  const priorityFeaturedClusters = SEO_TOPIC_CLUSTERS.filter((cluster) => priorityFeaturedClusterSlugs.has(cluster.slug));
  const featuredTopicClusters = [
    ...priorityFeaturedClusters,
    ...SEO_TOPIC_CLUSTERS.filter((cluster) => !priorityFeaturedClusterSlugs.has(cluster.slug)).slice(0, 8 - priorityFeaturedClusters.length),
  ];

  const reviewSignals = [
    {
      title: 'Original calculator-backed guidance',
      body: 'Core pages pair a working calculator with explanation, scenario context, assumptions, limitations, and related reading instead of publishing formula-only pages.',
      to: '/guides',
      cta: 'Read guides',
      icon: BookOpen,
    },
    {
      title: 'Transparent methodology',
      body: 'The methodology page explains how estimates are created, why results may differ from official systems, and when users should verify figures elsewhere.',
      to: '/methodology',
      cta: 'View methodology',
      icon: FileCheck2,
    },
    {
      title: 'Visible editorial standards',
      body: 'The editorial policy covers source checks, AI-assisted drafting review, corrections, monetization independence, and no-advice disclaimers for YMYL topics.',
      to: '/editorial-policy',
      cta: 'Editorial policy',
      icon: ShieldCheck,
    },
    {
      title: 'Privacy, cookies, and contact',
      body: 'Privacy, cookie controls, terms, and a public contact route are linked across the site so users can manage consent and report corrections or issues.',
      to: '/contact',
      cta: 'Contact page',
      icon: Globe,
    },
  ];
  const publicationStandards = [
    {
      label: 'Market scope',
      value: 'North America and Europe first',
      detail: 'Primary coverage is organized around US, Canada, UK, Europe, Germany, France, and Spain.',
    },
    {
      label: 'Source policy',
      value: 'Official sources preferred',
      detail: 'Tax, payroll, consumer-finance, and statutory claims are linked to official or reputable references where possible.',
    },
    {
      label: 'Advertising policy',
      value: 'Consent-gated ad loading',
      detail: 'Google AdSense is separated from essential site operation and loads only after advertising-cookie consent.',
    },
    {
      label: 'Corrections',
      value: 'Public correction route',
      detail: 'Users can report calculation errors, broken links, outdated references, privacy issues, or public report concerns by email.',
    },
  ];

  return (
    <div className="space-y-12 lg:space-y-16 pb-24">
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto border-b border-slate-200 pb-24 text-center flex flex-col items-center">
          <h1 
            className="serif text-5xl lg:text-7xl font-light text-slate-900 tracking-tight leading-[1.1] mb-8"
          >
            Financial <br />
            <span className="font-bold italic">Answer Engines.</span>
          </h1>
          <p 
            className="max-w-2xl text-xl text-slate-500 leading-relaxed mb-12"
          >
            Financial calculators and guides for North America and Europe, built around taxes, salary, VAT, mortgages, debt, savings, investing, and business scenarios.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-20">
             {PRIMARY_MARKET_CODES.map((code) => {
                const market = MARKET_PROFILES[code];
                return (
                  <Link key={market.code} to={market.routePrefix} className="px-6 py-3 bg-white border border-slate-200 rounded-full text-[10px] font-bold uppercase tracking-widest hover:border-slate-900 transition-all flex items-center">
                    <span className="w-4 h-4 mr-2 bg-blue-100 rounded-full flex items-center justify-center text-[7px] text-blue-700">{market.code}</span>
                    {market.navLabel}
                  </Link>
                );
             })}
             <Link to="/tools" className="px-6 py-3 bg-slate-900 text-white rounded-full text-[10px] font-bold uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center">
                <Globe className="w-3 h-3 mr-2" />
                Global Tools
             </Link>
          </div>

          {/* Large Hero Search Bar */}
          <div
            className="w-full max-w-3xl mb-12 relative"
          >
            <form onSubmit={handleSearch} className="relative group">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tools or topics..."
                className="w-full pl-8 pr-20 py-6 rounded-2xl bg-white border border-slate-200 text-slate-900 shadow-xl shadow-slate-100 hover:border-slate-900 transition-all focus:outline-none focus:ring-2 focus:ring-slate-900 group-hover:scale-[1.01]"
              />
              <button type="submit" aria-label="Search guides and tools" className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors">
                <Search className="h-6 w-6" />
              </button>
            </form>
          </div>

          <div 
            className="flex flex-wrap justify-center gap-6"
          >
            <Link to="/tools" className="px-10 py-5 bg-slate-900 text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-slate-800 transition-all flex items-center group shadow-lg shadow-slate-200">
              Try the Calculators
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/guides" className="px-10 py-5 bg-white text-slate-900 border border-slate-200 rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-slate-50 transition-all shadow-lg shadow-slate-50">
              Start Learning
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4">
        <div className="border-b border-slate-200 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <span className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-4 block">
                Platform purpose
              </span>
              <h2 className="serif text-3xl sm:text-4xl lg:text-5xl font-light text-slate-900 leading-tight break-words">
                What this site helps you calculate.
              </h2>
              <p className="mt-6 text-slate-500 leading-relaxed">
                The site turns real financial questions into transparent calculations, then explains
                the formula, assumptions, limitations, and practical context behind the answer.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/about" className="inline-flex items-center rounded-full bg-slate-900 px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-white">
                  About the site <ArrowRight className="ml-2 h-3 w-3" />
                </Link>
                <Link to="/editorial-policy" className="inline-flex items-center rounded-full border border-slate-200 bg-white px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-900">
                  Editorial Policy
                </Link>
              </div>
            </div>

            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {platformPillars.map(({ title, body, icon: Icon }) => (
                <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="mb-5 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-900">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-500">{body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 border-t border-slate-200 pt-8">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Topic clusters</p>
                <h3 className="mt-2 serif text-2xl font-bold text-slate-900">Start with the broader guide path, then open the calculator.</h3>
              </div>
              <Link to="/methodology" className="inline-flex items-center text-[10px] font-bold uppercase tracking-widest text-slate-900">
                Methodology <FileCheck2 className="ml-2 h-3.5 w-3.5" />
              </Link>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            {topicClusters.map((cluster) => (
                <div key={cluster.title} className="rounded-2xl bg-white p-5 border border-slate-100">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-900">{cluster.title}</h4>
                  <div className="mt-4 space-y-2">
                    {cluster.links.map((link) => (
                      <Link key={link.to} to={link.to} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-xs font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100">
                        {link.label}
                        <ArrowRight className="h-3 w-3 text-slate-300" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 border-t border-slate-200 pt-8">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600">High-intent search paths</p>
                <h3 className="mt-2 serif text-2xl font-bold text-slate-900">Specific questions are the first ranking targets.</h3>
              </div>
              <Link to="/topics" className="inline-flex items-center text-[10px] font-bold uppercase tracking-widest text-slate-900">
                View topic hubs <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              {featuredTopicClusters.map((cluster) => (
                <Link
                  key={cluster.slug}
                  to={getTopicClusterPath(cluster.slug)}
                  className="group rounded-2xl border border-slate-100 bg-slate-50 p-5 transition-colors hover:border-slate-900 hover:bg-white"
                >
                  <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{cluster.marketLabel}</span>
                  <h4 className="mt-3 text-sm font-bold leading-6 text-slate-900">{cluster.shortTitle}</h4>
                  <p className="mt-2 text-xs leading-5 text-slate-500">{cluster.searchIntents[0]}</p>
                  <span className="mt-4 inline-flex items-center text-[10px] font-bold uppercase tracking-widest text-slate-900">
                    Open hub <ArrowRight className="ml-2 h-3 w-3 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-4 block">
                Review readiness
              </span>
              <h2 className="serif text-3xl sm:text-4xl lg:text-5xl font-light text-slate-900 leading-tight">
                Built to be useful before it is monetized.
              </h2>
              <p className="mt-6 text-slate-500 leading-relaxed">
                FinancialMetrics.report is structured as an educational resource, not a thin
                calculator shell. The homepage, tool pages, guide library, and policy pages work
                together to show purpose, sources, editorial process, privacy choices, and limitations.
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {reviewSignals.map(({ title, body, to, cta, icon: Icon }) => (
                <Link
                  key={title}
                  to={to}
                  className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-colors hover:border-slate-300 hover:bg-white"
                >
                  <div className="mb-5 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-900 shadow-sm">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-500">{body}</p>
                  <span className="mt-5 inline-flex items-center text-[10px] font-bold uppercase tracking-widest text-slate-900">
                    {cta} <ArrowRight className="ml-2 h-3 w-3 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
          <div className="mt-10 rounded-3xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              {publicationStandards.map((standard) => (
                <div key={standard.label} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{standard.label}</p>
                  <h3 className="mt-3 text-sm font-bold text-slate-900">{standard.value}</h3>
                  <p className="mt-3 text-xs leading-5 text-slate-500">{standard.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Latest Reports Section */}
      <section ref={latestSectionRef} className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4 block">Guides</span>
            <h2 className="serif text-4xl lg:text-5xl font-light text-slate-900 leading-tight">Latest Guides <br /><span className="font-bold italic">& Insights.</span></h2>
          </div>
          <Link to="/guides" className="flex items-center text-[10px] font-bold uppercase tracking-widest text-slate-900 group border-b border-slate-900 pb-1">
            View Guides <ArrowRight className="ml-2 h-3 w-3 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {!shouldLoadLatest ? null : loading
            ? Array.from({ length: 6 }).map((_, index) => (
              <div key={`latest-guide-skeleton-${index}`} className="flex flex-col animate-pulse">
                <div className="mb-6 aspect-[16/10] rounded-2xl bg-slate-100" />
                <div className="h-3 w-24 rounded-full bg-slate-100 mb-4" />
                <div className="h-7 w-11/12 rounded bg-slate-100 mb-3" />
                <div className="h-7 w-4/5 rounded bg-slate-100 mb-6" />
                <div className="h-4 w-full rounded bg-slate-100 mb-2" />
                <div className="h-4 w-2/3 rounded bg-slate-100 mb-6" />
                <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                  <div className="h-3 w-20 rounded bg-slate-100" />
                  <div className="h-3 w-24 rounded bg-slate-100" />
                </div>
              </div>
            ))
            : latestArticles.map((article) => (
              <div 
                key={article.id}
                className="group flex flex-col [content-visibility:auto] [contain-intrinsic-size:520px]"
              >
                <Link to={article.route || `/guides/${article.slug}`} aria-label={`Open guide: ${article.title}`} className="mb-6 block overflow-hidden rounded-2xl relative">
                   <SmartImage 
                      src={getArticleImage(article)} 
                      alt={article.title} 
                      className="group-hover:scale-105 transition-transform duration-700" 
                      aspectRatio="aspect-[16/10]"
                   />
                   <span className="absolute left-4 top-4 rounded-full bg-slate-950/80 px-3 py-1.5 text-[8px] font-bold uppercase tracking-widest text-white">
                     FinancialMetrics.report
                   </span>
                </Link>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 block group-hover:text-slate-900 transition-colors">
                  {article.category}
                </span>
                <h3 className="serif text-2xl font-semibold text-slate-900 mb-4 group-hover:text-slate-600 transition-colors leading-tight">
                  <Link to={article.route || `/guides/${article.slug}`}>{article.title}</Link>
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-6 line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  <span className="flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    8 min read
                  </span>
                  <Link to={article.route || `/guides/${article.slug}`} aria-label={`Read guide: ${article.title}`} className="text-slate-900 flex items-center group-hover:translate-x-1 transition-transform">
                    Read Guide <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                </div>
              </div>
            ))}
        </div>
      </section>

      {/* Tools CTA */}
      <section className="max-w-7xl mx-auto px-4">
        <div className="bg-slate-900 rounded-3xl p-12 lg:p-20 text-white relative overflow-hidden flex flex-col lg:flex-row items-center gap-12">
            <div className="relative z-10 flex-1">
               <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px] mb-4 block">High-Utility Tools</span>
               <h2 className="serif text-4xl lg:text-6xl font-light mb-8 leading-tight">Complex modeling, <br /><span className="font-bold italic">elegantly simple.</span></h2>
               <p className="text-slate-400 max-w-md mb-8 leading-relaxed">
                 Toggle variables in real-time and observe the immediate impact on your long-term financial trajectory.
               </p>
               <Link to="/tools" className="inline-flex items-center px-8 py-4 bg-white text-slate-900 rounded-full font-bold uppercase tracking-widest text-[10px] hover:bg-slate-100 transition-colors">
                  Try the Calculators <ArrowRight className="ml-2 h-3 w-3" />
               </Link>
            </div>
            <div className="relative z-10 flex-1 w-full max-w-sm lg:max-w-none">
               <div className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 rotate-3 transform translate-y-6 lg:translate-x-12">
                  <div className="flex justify-between items-center mb-10">
                    <div className="w-12 h-2 bg-white/20 rounded-full"></div>
                    <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center font-serif italic text-white/40 font-bold">$</div>
                  </div>
                  <div className="space-y-6">
                     <div className="h-1 bg-white/10 w-full rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 w-2/3"></div>
                     </div>
                     <div className="h-1 bg-white/10 w-full rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 w-1/2"></div>
                     </div>
                     <div className="pt-4 flex justify-between items-end">
                        <div className="text-3xl font-serif font-bold text-white leading-none">$19,671</div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Growth Forecast</div>
                     </div>
                  </div>
               </div>
            </div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4"></div>
        </div>
      </section>
    </div>
  );
};

export default function App() {
  return (
    <Router>
      <RouteMeta />
      <ScrollToTop />
      <AnalyticsPageView />
      <Layout>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/knowledge" element={<Navigate to="/guides" replace />} />
            <Route path="/knowledge/:slug" element={<LegacyKnowledgeRedirect />} />
            
            {/* Global Utility Routes */}
            <Route path="/tools" element={<ToolsDirectory country="Global" />} />
            <Route path="/tools/:slug" element={<ToolPage />} />
            <Route path="/reports/:calculatorSlug/:reportId" element={<ReportPage />} />
            <Route path="/topics" element={<TopicClusterList />} />
            <Route path="/topics/:slug" element={<TopicClusterPage />} />
            <Route path="/guides" element={<ArticleList />} />
            <Route path="/guides/page/:pageNumber" element={<ArticleList />} />
            <Route path="/guides/:slug" element={<ArticleDetail />} />

            {/* US Section */}
            <Route path="/us" element={<ToolsDirectory country="US" />} />
            <Route path="/us/tools" element={<ToolsDirectory country="US" />} />
            <Route path="/us/tools/:slug" element={<ToolPage />} />
            <Route path="/us/reports/:calculatorSlug/:reportId" element={<ReportPage />} />
            <Route path="/us/guides" element={<ArticleList country="US" />} />
            <Route path="/us/guides/page/:pageNumber" element={<ArticleList country="US" />} />
            <Route path="/us/guides/:slug" element={<ArticleDetail />} />

            {/* UK Section */}
            <Route path="/uk" element={<ToolsDirectory country="UK" />} />
            <Route path="/uk/tools" element={<ToolsDirectory country="UK" />} />
            <Route path="/uk/tools/:slug" element={<ToolPage />} />
            <Route path="/uk/reports/:calculatorSlug/:reportId" element={<ReportPage />} />
            <Route path="/uk/guides" element={<ArticleList country="UK" />} />
            <Route path="/uk/guides/page/:pageNumber" element={<ArticleList country="UK" />} />
            <Route path="/uk/guides/:slug" element={<ArticleDetail />} />

            {/* Canada Section */}
            <Route path="/canada" element={<ToolsDirectory country="CA" />} />
            <Route path="/canada/tools" element={<ToolsDirectory country="CA" />} />
            <Route path="/canada/tools/:slug" element={<ToolPage />} />
            <Route path="/canada/reports/:calculatorSlug/:reportId" element={<ReportPage />} />
            <Route path="/canada/guides" element={<ArticleList country="CA" />} />
            <Route path="/canada/guides/page/:pageNumber" element={<ArticleList country="CA" />} />
            <Route path="/canada/guides/:slug" element={<ArticleDetail />} />

            {/* Europe Section */}
            <Route path="/europe" element={<ToolsDirectory country="EU" />} />
            <Route path="/europe/tools" element={<ToolsDirectory country="EU" />} />
            <Route path="/europe/tools/:slug" element={<ToolPage />} />
            <Route path="/europe/reports/:calculatorSlug/:reportId" element={<ReportPage />} />
            <Route path="/europe/guides" element={<ArticleList country="EU" />} />
            <Route path="/europe/guides/page/:pageNumber" element={<ArticleList country="EU" />} />
            <Route path="/europe/guides/:slug" element={<ArticleDetail />} />
            <Route path="/eu" element={<Navigate to="/europe" replace />} />
            <Route path="/eu/tools" element={<Navigate to="/europe/tools" replace />} />
            <Route path="/eu/guides" element={<Navigate to="/europe/guides" replace />} />
            <Route path="/eu/guides/page/:pageNumber" element={<EuropeGuidesPageRedirect />} />
            <Route path="/eu/vat" element={<Navigate to="/tools/vat-calculator" replace />} />

            {/* Major Europe Country Sections */}
            <Route path="/de" element={<ToolsDirectory country="DE" />} />
            <Route path="/de/tools" element={<ToolsDirectory country="DE" />} />
            <Route path="/de/tools/:slug" element={<ToolPage />} />
            <Route path="/de/guides" element={<ArticleList country="DE" />} />
            <Route path="/de/guides/page/:pageNumber" element={<ArticleList country="DE" />} />
            <Route path="/de/guides/:slug" element={<ArticleDetail />} />

            <Route path="/fr" element={<ToolsDirectory country="FR" />} />
            <Route path="/fr/tools" element={<ToolsDirectory country="FR" />} />
            <Route path="/fr/tools/:slug" element={<ToolPage />} />
            <Route path="/fr/guides" element={<ArticleList country="FR" />} />
            <Route path="/fr/guides/page/:pageNumber" element={<ArticleList country="FR" />} />
            <Route path="/fr/guides/:slug" element={<ArticleDetail />} />

            <Route path="/es" element={<ToolsDirectory country="ES" />} />
            <Route path="/es/tools" element={<ToolsDirectory country="ES" />} />
            <Route path="/es/tools/:slug" element={<ToolPage />} />
            <Route path="/es/guides" element={<ArticleList country="ES" />} />
            <Route path="/es/guides/page/:pageNumber" element={<ArticleList country="ES" />} />
            <Route path="/es/guides/:slug" element={<ArticleDetail />} />

            {/* ZM Section */}
            <Route path="/zm" element={<ToolsDirectory country="ZM" />} />
            <Route path="/zm/tools" element={<ToolsDirectory country="ZM" />} />
            <Route path="/zm/tools/:slug" element={<ToolPage />} />
            <Route path="/zm/reports/:calculatorSlug/:reportId" element={<ReportPage />} />
            <Route path="/zm/guides" element={<ArticleList country="ZM" />} />
            <Route path="/zm/guides/page/:pageNumber" element={<ArticleList country="ZM" />} />
            <Route path="/zm/guides/:slug" element={<ArticleDetail />} />

            {/* Africa Regional Section */}
            <Route path="/africa/guides" element={<ArticleList />} />
            <Route path="/africa/guides/page/:pageNumber" element={<ArticleList />} />
            <Route path="/africa/guides/:slug" element={<ArticleDetail />} />

            <Route path="/methodology" element={<AboutData />} />
            <Route path="/about-the-data" element={<Navigate to="/methodology" replace />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/editorial-policy" element={<EditorialPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/contact" element={<Contact />} />

            {/* Dynamic Catch-all for Jurisdictional Reports */}
            <Route path="/:countryCode/reports/:calculatorSlug/:reportId" element={<ReportPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/new" element={<ArticleEditor />} />
            <Route path="/admin/edit/:id" element={<ArticleEditor />} />
            <Route path="/admin/categories" element={<CategoryManager />} />
            <Route path="/admin/calculators" element={<CalculatorManager />} />
            <Route path="/admin/jurisdictions" element={<JurisdictionManager />} />
            <Route path="/admin/seo" element={<SeoIntelligence />} />
          </Routes>
        </Suspense>
      </Layout>
    </Router>
  );
}
